/**
 * The game-start screen's active-run state and the visuals it drives — the mode-card halo, the
 * per-card "Runde N" badge, and the leaderboard section's reveal-when-earned gate.
 *
 * Since DB slice 2b the runs are sourced from the acting player's server-side corpus (`GET
 * /api/runs`, design § The restore-authority flip) instead of a localStorage scan — the server is
 * restore-authoritative, so the Continue affordance follows its data, not the client's own copy.
 * The map starts empty at render time and is patched in once the fetch resolves (`loadActiveRuns`,
 * fire-and-forget — the same pattern `leaderboard/leaderboard.js` uses for its own async fetch),
 * shared across mode click handlers, halo, and badge so they stay in sync without re-fetching on
 * every click.
 */
import { t } from "../i18n.js";
import { getApi } from "../api.js";
import { renderLeaderboardInto } from "../leaderboard/leaderboard.js";
import { playLeaderboardReveal } from "./game-start-entrance.js";

/** gameMode → completed-round count for the current render. */
let activeRuns = new Map();

/** Monotonic guard so a superseded renderGameStart's runs-fetch never patches a torn-down screen. */
let runsRequestSeq = 0;

/**
 * The in-flight (or already-settled) `GET /api/runs` fetch — starts pre-resolved so a Play click
 * before the first `renderGameStart` never hangs. `game-start-session.js#startSession` awaits this
 * (via `hooks.runsReady`) before reading `hasActiveRun`, so a fast click landing before the fetch
 * resolves can't route on a stale/empty map and fork a duplicate run (2b review gap 2, the gap-2
 * fix) — `renderGameStart` itself stays synchronous; only the Play routing waits.
 */
let runsReady = Promise.resolve();

/** True when the acting player has a run in progress for the mode (Continue is offered). */
export function hasActiveRun(gameMode) {
  return activeRuns.has(gameMode);
}

/** Empty the map for a fresh render; loadActiveRuns patches the real data in when it resolves. */
export function resetActiveRuns() {
  activeRuns = new Map();
}

/** True when any mode has at least one completed round — the leaderboard is earned. */
function hasPlayedAnyRound() {
  for (const count of activeRuns.values()) if (count >= 1) return true;
  return false;
}

/**
 * GET /api/runs → gameMode → completed-round count. Failures propagate: an unknown run state must
 * reach the Play routing as an error, never read as "no active runs" — routing on an empty map
 * after a failed fetch is the same duplicate-run fork the gap-2 await exists to prevent.
 */
async function fetchActiveRuns() {
  const rows = await getApi("runs");
  const runs = new Map();
  for (const row of rows) runs.set(row.gameMode, row.roundNumber);
  return runs;
}

/**
 * Fire-and-forget: fetches the acting player's runs and patches the already-rendered screen once
 * they arrive — renderGameStart itself must stay synchronous (it's called from the fully
 * synchronous render() pipeline in game.js). The `seq` guard drops a stale response from a
 * renderGameStart call the DOM has since moved past (e.g. a rapid Quit → game-start round-trip).
 */
export function loadActiveRuns(settings) {
  const seq = ++runsRequestSeq;
  runsReady = fetchActiveRuns().then((runs) => {
    if (seq !== runsRequestSeq) return;
    activeRuns = runs;
    refreshActiveRunVisuals(settings);
  });
  // The render-path caller is fire-and-forget: log the failure here, but leave `runsReady` itself
  // rejected so the Play routing sees an error rather than an empty map (no silent fork).
  runsReady.catch((e) =>
    console.warn("[game-start] /api/runs fetch failed:", e?.message ?? e),
  );
  return runsReady;
}

/** Resolves once the latest `GET /api/runs` fetch has settled — the Play-routing await. */
export function activeRunsReady() {
  return runsReady;
}

/** "Runde 13" — the in-progress run's badge text, shown on its mode card. */
function runBadgeText(roundCount) {
  // roundCount is the completed-round count from the serde corpus. The badge points at the round
  // *about to be played* (matches DESIGN.md § 13 example "Round 13"). The cumulative score that
  // used to follow it is a backend-derived forced fact, no longer stored client-side (slice 4).
  return t("gameStartRunBadge", roundCount + 1);
}

// why: a non-breaking space, not "" or a plain space (which HTML collapses), reserves the badge's
// line box so a mode card's geometry is identical with and without an active run — the reserved
// line keeps card geometry independent of the async /api/runs patch, so the entrance's measured
// etch frames and everything below the cards never move when the badge text lands.
const MODE_RUN_BADGE_PLACEHOLDER = "\u00a0";

/**
 * Sync a mode card's run-badge text to its active run (if any). The `.mode-run-badge` element
 * itself is created once, unconditionally, in game-start-ui.js#createModeCard — this only ever
 * sets textContent, never adds or removes the element.
 */
export function updateModeCardBadge(card) {
  const roundCount = activeRuns.get(card.dataset.mode);
  const badge = card.querySelector(".mode-run-badge");
  badge.textContent =
    roundCount == null ? MODE_RUN_BADGE_PLACEHOLDER : runBadgeText(roundCount);
}

/**
 * Refresh the active-run halo + per-card run badge + leaderboard on every mode change and once the
 * `GET /api/runs` fetch resolves. The leaderboard section itself is reveal-when-earned (a newcomer
 * gets no section, no fetch) — since `activeRuns` starts empty and only the async fetch can prove
 * a completed round exists, this is also the section's sole creation point since slice 2b (moved
 * out of the synchronous `buildGameStartContent`, which can no longer know in time).
 */
export function refreshActiveRunVisuals(settings) {
  const screen = document.querySelector(".game-start");
  if (screen == null) return;
  for (const card of screen.querySelectorAll(".mode-card")) {
    card.classList.toggle("has-active-run", hasActiveRun(card.dataset.mode));
    updateModeCardBadge(card);
  }
  let leaderboard = screen.querySelector(".game-start-leaderboard");
  if (!hasPlayedAnyRound()) {
    if (leaderboard != null) leaderboard.remove();
    return;
  }
  if (leaderboard == null) {
    leaderboard = buildLeaderboardSection(settings);
    screen
      .querySelector(":scope > .game-start-content")
      .appendChild(leaderboard);
    return; // buildLeaderboardSection already fired the initial renderLeaderboardInto
  }
  renderLeaderboardInto(leaderboard, settings, {
    onFirstRender: playLeaderboardReveal,
  });
}

function buildLeaderboardSection(settings) {
  const section = document.createElement("div");
  section.className = "game-start-leaderboard";
  // The start-screen stamp (leaderboard-dedicated-page-design § Analytics) — read by
  // leaderboard-render.js's leaderboard-inspected track call via closest("[data-lb-surface]").
  section.dataset.lbSurface = "start";
  // Fire-and-forget: the module guards stale responses internally. onFirstRender replaces the
  // old hardwired playLeaderboardReveal import inside the (now-shared) render module — this is
  // the one caller that still wants the once-per-container cascade.
  renderLeaderboardInto(section, settings, {
    onFirstRender: playLeaderboardReveal,
  });
  return section;
}
